import React, { ReactElement, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import {
  Body,
  Button,
  GlobalTemplate,
  Headline,
  Input,
  Link,
  Meta,
  Textarea,
} from '../../components';
import { track } from '../../utilities';

export default function RequestSidekick(): ReactElement {
  const [defaultEmail, setDefaultEmail] = useState('');
  const [showYourEmailError, setShowYourEmailError] = useState(false);
  const [showAdminEmailError, setShowAdminEmailError] = useState(false);
  const [showMessageError, setShowMessageError] = useState(false);
  const [showErrorPrompt, setShowErrorPrompt] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const submitSalesForm = async (e) => {
    const form = document.getElementById('form');
    e.preventDefault();
    e.stopPropagation();
    //@ts-ignore
    let formData = new FormData(form);
    const payload: Record<string, FormDataEntryValue> = {};
    const validRegex =
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;

    let hasError = false;
    for (var [key, value] of formData.entries()) {
      if (
        (key === 'yourEmail' && value === '') ||
        //@ts-ignore
        (key === 'yourEmail' && !value.match(validRegex))
      ) {
        setShowYourEmailError(true);
        hasError = true;
      } else if (
        (key === 'adminEmail' && value === '') ||
        //@ts-ignore
        (key === 'adminEmail' && !value.match(validRegex))
      ) {
        setShowAdminEmailError(true);
        hasError = true;
      } else if (key === 'message' && value === '') {
        setShowMessageError(true);
        hasError = true;
      } else {
        payload[key] = value;
      }
    }

    if (hasError) {
      setShowErrorPrompt(true);
    } else {
      setShowErrorPrompt(false);
      setShowYourEmailError(false);
      setShowAdminEmailError(false);
      setShowMessageError(false);

      let response = await fetch(
        '/.netlify/functions/send-request-sidekick-form',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );

      response = await response.json();

      //@ts-ignore
      if (response.message === 'Email sent') {
        setShowSuccessMessage(true);
        if (typeof window !== 'undefined') {
          track('Request Sidekick submitted', {
            sender: formData.get('yourEmail'),
            receiver: formData.get('adminEmail'),
          });
        }
      }
    }
  };

  useEffect(() => {
    let search = window.location.search;
    let params = new URLSearchParams(search);
    let email = params.get('email');
    if (email) {
      setDefaultEmail(email);
    }
  }, []);

  return (
    <GlobalTemplate
      pageBgColor="bone"
      navBgColor="bone"
      hideFooter
      hideNavigation>
      <main>
        <div className="flex justify-center p-4">
          <div className="mb-52 max-w-125">
            <div className="relative mt-10">
              <svg
                width="941"
                height="967"
                viewBox="0 0 941 967"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="pointer-events-none absolute -top-24 -left-100 hidden blur-[162px] will-change-[filter] md:block"
                colorInterpolationFilters="sRGB">
                <circle
                  cx="563.967"
                  cy="494.98"
                  r="168.445"
                  fill="#7662F6"></circle>
                <circle
                  cx="748.609"
                  cy="494.978"
                  r="168.445"
                  fill="#4A9EF4"></circle>
                <circle
                  cx="494.861"
                  cy="651.547"
                  r="168.445"
                  fill="#E05534"></circle>
                <circle
                  cx="563.967"
                  cy="743.328"
                  r="168.445"
                  fill="#E98535"></circle>
                <circle
                  cx="732.411"
                  cy="743.328"
                  r="168.445"
                  fill="#F9D749"></circle>
                <circle
                  cx="805.835"
                  cy="604.037"
                  r="168.445"
                  fill="#5CC183"></circle>
              </svg>
              <form
                onSubmit={submitSalesForm}
                id="form"
                name="Sales form"
                className="sales-form relative z-50">
                <img className="mx-auto mb-6 w-16" alt="" src="/icon-192.png" />
                <Headline level="2" color="blue" className="text-center">
                  Request Sidekick Access
                </Headline>
                <div
                  className={twMerge(
                    'mt-12 grid gap-4 rounded-xl bg-white p-12 shadow-md transition-opacity',
                    showSuccessMessage ? 'hidden opacity-0' : 'opacity-100'
                  )}>
                  <Input
                    type="email"
                    label="Your email"
                    name="yourEmail"
                    placeholder="jane@yourcompany.com"
                    isRequired
                    defaultValue={defaultEmail}
                    status={showYourEmailError ? 'error' : 'default'}
                    helpText={showYourEmailError ? 'Provide your email' : ''}
                  />

                  <Input
                    type="email"
                    label="Admin / Manager's email"
                    name="adminEmail"
                    placeholder="jane@yourcompany.com"
                    isRequired
                    status={showAdminEmailError ? 'error' : 'default'}
                    helpText={
                      showAdminEmailError ? "Provide your admin's email" : ''
                    }
                  />

                  <Textarea
                    label="Message to your admin / manager"
                    name="message"
                    placeholder="Message to your admin / manager"
                    defaultValue="I’d love access to Sidekick to automatically scan and fix accessibility issues in our design files."
                    rows={12}
                    status={showMessageError ? 'error' : 'default'}
                    helpText={
                      showMessageError
                        ? 'Provide a message to your admin / manager'
                        : ''
                    }
                  />

                  <div className="mt-6">
                    <Button isBlock>Send Request</Button>

                    <Link
                      className="mt-4 inline-block w-full text-center"
                      href="/pricing">
                      Start a free trial
                    </Link>
                  </div>

                  {showErrorPrompt && (
                    <div>
                      <Body color="error" className="text-med!">
                        Please fix the errors above before submitting.
                      </Body>
                    </div>
                  )}

                  <Body
                    color="gray"
                    className="mt-2 border-t border-gray-200 px-8 pt-4 text-center text-sm!"
                    style={{ textWrap: 'pretty' }}>
                    By clicking submit, you agree to our{' '}
                    <Link href="/tos">Terms of Service</Link> and{' '}
                    <Link href="/privacy">Privacy Policy</Link>.
                  </Body>
                </div>

                <div
                  className={twMerge(
                    'mt-10 grid gap-4 rounded-lg bg-white px-16 py-32 text-center transition-opacity',
                    showSuccessMessage ? 'opacity-100' : 'hidden opacity-0'
                  )}>
                  <img className="mb-6" alt="" src="/stark-confetti.png" />
                  <Body>
                    A request for Sidekick access has been sent to your manager
                    or team admin!
                  </Body>

                  <Body>
                    <Link href="/">Go Home</Link>
                  </Body>
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>
    </GlobalTemplate>
  );
}

export const Head = () => (
  <Meta ogImgUrl="https://res.cloudinary.com/stark-lab/image/upload/v1761273066/marketing/meta/homepage_fzpkxd.png">
    <meta name="theme-color" content="#381FD1"></meta>
  </Meta>
);
